/** UUID v4 pattern — treat as non–user-facing when used as display fallback. */
const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function isUuidLike(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  const s = String(value).trim();
  return s.length > 0 && UUID_RE.test(s);
}

export const DISPLAY_PLACEHOLDER = '—';

export type DisplayLabelsOptions = {
  /** When true, UUID-shaped strings may be shown (e.g. admin technical). */
  technical?: boolean;
  empty?: string;
};

function normalizeParts(parts: Array<string | null | undefined>): string {
  return parts
    .map((p) => (p == null ? '' : String(p).trim()))
    .filter(Boolean)
    .join(' — ');
}

/**
 * Prefer business code/number, then name; never surface raw UUID in normal mode.
 */
export function formatEntityReference(
  parts: { code?: string | null; name?: string | null; id?: string | null },
  opts: DisplayLabelsOptions = {}
): string {
  const empty = opts.empty ?? DISPLAY_PLACEHOLDER;
  const code = parts.code?.trim();
  const name = parts.name?.trim();
  const id = parts.id?.trim();

  const composite = normalizeParts([code, name]);
  if (composite) return composite;
  if (name) return name;
  if (code) return code;

  if (id && opts.technical) return id;
  if (id && !isUuidLike(id)) return id;

  return empty;
}

/**
 * Document reference label (PO, invoice, GRN, SAR, PV, etc.).
 * Prefer business number, then name; never show raw UUID unless `technical`.
 */
export function formatDocumentRef(
  parts: { number?: string | null; name?: string | null; id?: string | null },
  opts: DisplayLabelsOptions = {}
): string {
  return formatEntityReference(
    { code: parts.number, name: parts.name, id: parts.id },
    opts
  );
}

export function getDocumentDisplayNumber(
  primary: string | null | undefined,
  opts: DisplayLabelsOptions = {}
): string {
  const empty = opts.empty ?? DISPLAY_PLACEHOLDER;
  const s = primary?.trim();
  if (s && !isUuidLike(s)) return s;
  if (s && opts.technical) return s;
  return empty;
}

/** Strip UUID from a list of fallback strings; return first safe non-UUID text. Optional last arg: `{ empty, technical }`. */
export function firstNonUuidDisplay(
  ...args: Array<string | null | undefined | DisplayLabelsOptions>
): string {
  const list = [...args];
  let opts: DisplayLabelsOptions = {};
  const last = list[list.length - 1];
  if (
    last !== null &&
    typeof last === 'object' &&
    !Array.isArray(last) &&
    ('empty' in last || 'technical' in last)
  ) {
    opts = list.pop() as DisplayLabelsOptions;
  }
  const empty = opts.empty ?? DISPLAY_PLACEHOLDER;
  for (const c of list as Array<string | null | undefined>) {
    const s = c?.trim();
    if (!s) continue;
    if (!isUuidLike(s)) return s;
  }
  return empty;
}

export function getPartyDisplayLabel(
  name?: string | null,
  code?: string | null,
  idFallback?: string | null,
  opts: DisplayLabelsOptions = {}
): string {
  return formatEntityReference({ name, code, id: idFallback }, opts);
}
